/**
 * BulkUploadPage — import many questions from a CSV, Excel or JSON file, one
 * guided step at a time: Choose a file → Match columns (a sheet only) → Check
 * and upload → Done. The step on screen is derived from what has been read, so
 * the bar at the top is a read-out, never a second source of truth.
 */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Upload, CheckCircle2, XCircle, Download, FileJson, FileSpreadsheet,
  ArrowLeft, ArrowRight, FolderTree,
} from "lucide-react";
import * as XLSX from "xlsx";
import apiClient from "../../utils/apiClient";
import {
  PageHeader, AppButton, AppBadge, AppCard, AppCardContent, AppDataTable, Callout, DropZone,
  FormField, NativeSelect, StepRail, cn,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import { QUESTION_TYPE_LABELS } from "./quizBankRules";
import { uploadTrail } from "./quizCenterTrail";

// Up to 6 options (A–F) are supported per question.
const OPTION_KEYS = ["optionA", "optionB", "optionC", "optionD", "optionE", "optionF"];

// Only these question types carry options; others (numeric/text/fill-blank) don't.
const CHOICE_TYPE_KEYS = new Set([
  "single_choice", "multi_select", "true_false", "image_mcq", "code_conceptual",
]);

// Target DB fields the importer can map arbitrary spreadsheet columns onto.
const TARGET_FIELDS = [
  { key: "questionText", label: "Question text", required: true },
  { key: "questionType", label: "Type" },
  { key: "difficulty", label: "Difficulty" },
  { key: "marks", label: "Marks" },
  { key: "negativeMarks", label: "Negative marks" },
  { key: "category", label: "Category" },
  { key: "subcategory", label: "Subcategory" },
  { key: "optionA", label: "Option A" },
  { key: "optionB", label: "Option B" },
  { key: "optionC", label: "Option C" },
  { key: "optionD", label: "Option D" },
  { key: "optionE", label: "Option E" },
  { key: "optionF", label: "Option F" },
  { key: "correctOptions", label: "Correct option(s)" },
  { key: "acceptedAnswers", label: "Accepted answers (text / fill-blank)" },
  { key: "numericAnswer", label: "Numeric answer (NAT)" },
  { key: "numericTolerance", label: "Numeric tolerance (±)" },
  { key: "explanation", label: "Explanation" },
  { key: "tags", label: "Tags" },
];

// Header synonyms for smart auto-mapping (normalized, no spaces/punctuation).
const FIELD_SYNONYMS = {
  questionText: ["question", "questiontext", "ques", "q", "problem", "statement"],
  questionType: ["type", "questiontype", "qtype", "format"],
  difficulty: ["difficulty", "level", "diff"],
  marks: ["marks", "mark", "points", "score", "weight"],
  negativeMarks: ["negativemarks", "negative", "negmarks", "penalty"],
  category: ["category", "topic", "subject", "section", "chapter"],
  subcategory: ["subcategory", "subcat", "subtopic", "subsection", "subchapter"],
  optionA: ["optiona", "option1", "opta", "a", "choicea", "choice1"],
  optionB: ["optionb", "option2", "optb", "b", "choiceb", "choice2"],
  optionC: ["optionc", "option3", "optc", "c", "choicec", "choice3"],
  optionD: ["optiond", "option4", "optd", "d", "choiced", "choice4"],
  optionE: ["optione", "option5", "opte", "e", "choicee", "choice5"],
  optionF: ["optionf", "option6", "optf", "f", "choicef", "choice6"],
  correctOptions: ["correctoptions", "correct", "answer", "correctanswer", "ans", "key", "correctoption"],
  acceptedAnswers: ["acceptedanswers", "accepted", "answers", "answerkey", "expectedanswer"],
  numericAnswer: ["numericanswer", "numeric", "numericalanswer", "natanswer", "value"],
  numericTolerance: ["numerictolerance", "tolerance", "tol", "margin", "range"],
  explanation: ["explanation", "explain", "solution", "rationale", "reason"],
  tags: ["tags", "tag", "labels", "keywords"],
};

const normalizeHeader = (h) => String(h || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Exact synonym match — a header whose normalized form equals one of the synonyms.
const exactHeader = (fieldKey, headers, claimed) => {
  const syns = FIELD_SYNONYMS[fieldKey] || [normalizeHeader(fieldKey)];
  return headers.find((h) => !claimed.has(h) && syns.includes(normalizeHeader(h))) || "";
};

// Fuzzy "contains" match — ONLY with synonyms long enough to be unambiguous.
// Single-letter synonyms ("a"/"e"/"f"…) must NEVER drive a contains match, or
// optionE matches "cat-e-gory" and optionF matches "di-f-ficulty" (every imported
// question then gets the category + difficulty appended as bogus options E/F).
const fuzzyHeader = (fieldKey, headers, claimed) => {
  const syns = (FIELD_SYNONYMS[fieldKey] || [normalizeHeader(fieldKey)]).filter((s) => s.length >= 4);
  if (!syns.length) return "";
  return headers.find((h) => {
    if (claimed.has(h)) return false;
    const n = normalizeHeader(h);
    return n.length >= 4 && syns.some((s) => n.includes(s) || s.includes(n));
  }) || "";
};

// Two passes: take all the strong EXACT matches first (claiming their header so no
// other field can steal it), then fuzzy-match only the still-unmapped fields against
// the remaining unclaimed headers.
const autoMap = (headers) => {
  const map = {};
  const claimed = new Set();
  for (const f of TARGET_FIELDS) {
    const m = exactHeader(f.key, headers, claimed);
    map[f.key] = m;
    if (m) claimed.add(m);
  }
  for (const f of TARGET_FIELDS) {
    if (map[f.key]) continue;
    const m = fuzzyHeader(f.key, headers, claimed);
    if (m) { map[f.key] = m; claimed.add(m); }
  }
  return map;
};

// Every question type we support, with what each needs in a bulk file. Shown on
// the page so uploaders know their options before mapping columns.
const SUPPORTED_TYPES = [
  { key: "single_choice", label: "Single Choice", needs: "Options A–F + correctOptions = one letter (e.g. B)." },
  { key: "multi_select", label: "Multi Select", needs: "Options A–F + correctOptions = comma list (e.g. A,C,E)." },
  { key: "true_false", label: "True / False", needs: "Option A = True, B = False + correctOptions = A or B." },
  { key: "image_mcq", label: "Image MCQ", needs: "Like Single Choice; option images set via JSON (imageUrl)." },
  { key: "code_conceptual", label: "Code (predict output)", needs: "Single-choice options; code snippet via JSON (codeSnippet)." },
  { key: "text_answer", label: "Text Answer", needs: "acceptedAnswers = comma-separated accepted answers." },
  { key: "fill_blank", label: "Fill in the Blank", needs: "Use ____ in the text + acceptedAnswers column." },
  { key: "numeric_answer", label: "Numeric (NAT)", needs: "numericAnswer (a number) + optional numericTolerance (±)." },
];

const SAMPLE_CSV = `category,subcategory,questionText,questionType,difficulty,marks,negativeMarks,optionA,optionB,optionC,optionD,correctOptions,acceptedAnswers,numericAnswer,numericTolerance,explanation,tags
"Java","OOP","Which keyword is used to inherit a class in Java?",single_choice,easy,1,0,"extends","implements","inherits","using","A","","","","extends is used for class inheritance","java,oop"
"Java","Collections","Which of these are part of the Java Collections Framework?",multi_select,medium,2,0.5,"ArrayList","HashMap","int","TreeSet","A,B,D","","","","int is a primitive, not a collection","java,collections"
"Java","Basics","Java is a purely object-oriented language.",true_false,easy,1,0,"True","False","","","B","","","","It has primitives, so it is not purely OO","java"
"NEET Chemistry","Mole Concept","How many moles are present in 36 g of water? (in mol)",numeric_answer,medium,4,1,"","","","","","","2","0.01","36 / 18 = 2 mol","neet,chemistry"
"Java","Keywords","The keyword used to define a constant in Java is ____",fill_blank,easy,1,0,"","","","","","final;static final","","","final makes a variable constant","java"`;

const SAMPLE_JSON = JSON.stringify(
  {
    questions: [
      {
        category: "Java",
        subcategory: "OOP",
        questionText: "Which keyword is used to inherit a class in Java?",
        questionType: "single_choice",
        difficulty: "easy",
        marks: 1,
        options: [
          { text: "extends",    isCorrect: true  },
          { text: "implements", isCorrect: false },
          { text: "inherits",   isCorrect: false },
          { text: "using",      isCorrect: false },
        ],
        explanation: "extends is used for class inheritance.",
        tags: ["java", "oop"],
      },
      {
        category: "NEET Chemistry",
        subcategory: "Mole Concept",
        questionText: "How many moles are present in 36 g of water?",
        questionType: "numeric_answer",
        difficulty: "medium",
        marks: 4,
        negativeMarks: 1,
        numericAnswer: 2,
        numericTolerance: 0.01,
        explanation: "36 / 18 = 2 mol.",
        tags: ["neet", "chemistry"],
      },
    ],
  },
  null,
  2
);

// The three difficulty bands, on the badge vocabulary. `easy`/`medium`/`hard`
// were three hand-picked greens/ambers/reds with their own dark-mode fork;
// AppBadge's `-soft`/`-fg` pairs already invert.
const DIFFICULTY_BADGE_COLORS = { easy: "emerald", medium: "amber", hard: "rose" };

/** A step card's title and the sentence under it. */
const SectionHead = ({ title, sub }) => (
  <div>
    <h2 className="tw-m-0 tw-text-[0.95rem] tw-font-semibold tw-text-content">{title}</h2>
    <p className="tw-m-0 tw-mt-[0.15rem] tw-text-[0.8125rem] tw-text-muted-fg">{sub}</p>
  </div>
);

/** Back on the left, the way forward on the right, under a step's content. */
const StepActions = ({ children }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-4">
    {children}
  </div>
);

const PREVIEW_COLUMNS = [
  {
    key: "index",
    label: "#",
    width: 36,
    className: "tw-text-content-muted tw-tabular-nums",
    render: (_row, i) => i + 1,
  },
  {
    key: "questionText",
    label: "Question",
    className: "tw-max-w-0 tw-truncate",
  },
  {
    key: "questionType",
    label: "Type",
    width: 120,
    render: (row) => <AppBadge size="sm" variant="secondary">{QUESTION_TYPE_LABELS[row.questionType] || row.questionType}</AppBadge>,
  },
  {
    key: "difficulty",
    label: "Difficulty",
    width: 90,
    render: (row) => (
      <AppBadge size="sm" color={DIFFICULTY_BADGE_COLORS[row.difficulty]} className="tw-capitalize">
        {row.difficulty}
      </AppBadge>
    ),
  },
  {
    key: "options",
    label: "Options",
    width: 70,
    className: "tw-text-content-muted tw-tabular-nums",
    render: (row) => row.options?.length || 0,
  },
];

const BulkUploadPage = () => {
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank";
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState([]);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  // Categories — every question needs one. A default is applied to any row that
  // doesn't name its own category (or names one we can't resolve).
  const [categories, setCategories] = useState([]);
  const [defaultCategory, setDefaultCategory] = useState("");

  // Column mapping (smart import) — raw spreadsheet rows + a field→column map.
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [mapping, setMapping] = useState({});

  useEffect(() => {
    apiClient.get("/quiz-categories?flat=true")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  // ── Raw CSV parser → { headers, rows } (rows keyed by header) ──────
  // RFC-4180 aware: a quoted field may contain commas AND newlines, and "" is an
  // escaped quote. We scan the whole text char-by-char tracking the quote state —
  // a naive line-by-line split breaks multi-line code cells (each code line turns
  // into a bogus row, which produces garbage categories).
  const parseCSVRaw = (text) => {
    let s = String(text).replace(/\r\n?/g, "\n");
    if (s.charCodeAt(0) === 0xFEFF) s = s.slice(1); // strip UTF-8 BOM if present
    const records = [];
    let row = [];
    let cur = "";
    let inQ = false;

    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (inQ) {
        if (ch === '"') {
          if (s[i + 1] === '"') { cur += '"'; i++; } // escaped quote ""
          else inQ = false;                          // closing quote
        } else {
          cur += ch;                                 // keep newlines/commas inside quotes
        }
      } else if (ch === '"') {
        inQ = true;
      } else if (ch === ",") {
        row.push(cur); cur = "";
      } else if (ch === "\n") {
        row.push(cur); records.push(row); row = []; cur = "";
      } else {
        cur += ch;
      }
    }
    if (cur !== "" || row.length > 0) { row.push(cur); records.push(row); }

    // Keep only records with at least one non-empty cell (drops blank trailing lines).
    const nonEmpty = records.filter((r) => r.some((c) => String(c).trim() !== ""));
    if (nonEmpty.length < 2) return { headers: [], rows: [] };

    const headers = nonEmpty[0].map((h) => h.trim());
    const rows = [];
    for (let r = 1; r < nonEmpty.length; r++) {
      const values = nonEmpty[r];
      const obj = {};
      headers.forEach((h, idx) => { obj[h] = String(values[idx] ?? "").trim(); });
      rows.push(obj);
    }
    return { headers, rows };
  };

  // ── Build questions from raw rows + the field→column mapping ───────
  const buildFromMapping = (rows, map) => {
    const out = [];
    rows.forEach((row) => {
      const get = (key) => (map[key] ? String(row[map[key]] ?? "").trim() : "");
      const questionText = get("questionText");
      if (!questionText) return;
      const questionType = get("questionType") || "single_choice";
      // Only choice-based types carry options. This stops any stray option column
      // from attaching answers to numeric / text / fill-blank questions.
      const opts = [];
      if (CHOICE_TYPE_KEYS.has(questionType)) {
        OPTION_KEYS.forEach((k) => { const v = get(k); if (v) opts.push({ text: v, isCorrect: false }); });
        get("correctOptions").toUpperCase().split(/[,;|]/).forEach((c) => {
          const t = c.trim();
          if (!t) return;
          const byLetter = t.charCodeAt(0) - 65;       // "A".."F"
          const byIndex = parseInt(t, 10) - 1;          // "1".."6"
          const idx = (byLetter >= 0 && byLetter < opts.length) ? byLetter : byIndex;
          if (opts[idx]) opts[idx].isCorrect = true;
        });
      }
      const numAns = get("numericAnswer");
      const numTol = get("numericTolerance");
      out.push({
        category: get("category"),
        subcategory: get("subcategory"),
        questionText,
        questionType,
        difficulty: (get("difficulty") || "medium").toLowerCase(),
        marks: parseFloat(get("marks")) || 1,
        negativeMarks: parseFloat(get("negativeMarks")) || 0,
        options: opts,
        acceptedAnswers: get("acceptedAnswers").split(/[,;|]/).map((a) => a.trim()).filter(Boolean),
        numericAnswer: numAns !== "" ? parseFloat(numAns) : undefined,
        numericTolerance: numTol !== "" ? parseFloat(numTol) : undefined,
        explanation: get("explanation"),
        tags: get("tags").split(/[,;|]/).map((t) => t.trim()).filter(Boolean),
      });
    });
    return out;
  };

  const applyMapping = () => {
    if (!mapping.questionText) {
      setErrors(["Map the “Question text” column to continue."]);
      return;
    }
    const qs = buildFromMapping(rawRows, mapping);
    if (qs.length === 0) {
      setErrors(["No questions produced — check your column mapping."]);
      return;
    }
    setErrors([]);
    setParsed(qs);
  };

  const consumeFile = (selected) => {
    setFile(selected);
    setResult(null);
    setErrors([]);
    setParsed([]);
    setRawHeaders([]); setRawRows([]); setMapping({});
    const name = selected.name.toLowerCase();
    const reader = new FileReader();

    if (name.endsWith(".json")) {
      reader.onload = (ev) => {
        try {
          const data = JSON.parse(ev.target.result);
          const qs = Array.isArray(data) ? data : data.questions || [];
          setParsed(qs);
          if (qs.length === 0) setErrors(["No questions found in JSON file"]);
        } catch { setErrors(["Invalid JSON file"]); }
      };
      reader.readAsText(selected);
      return;
    }

    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      reader.onload = (ev) => {
        try {
          const wb = XLSX.read(new Uint8Array(ev.target.result), { type: "array" });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const headerRow = XLSX.utils.sheet_to_json(ws, { header: 1 })[0] || [];
          const headers = headerRow.map((h) => String(h)).filter(Boolean);
          const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
          if (rows.length === 0) { setErrors(["No rows found in the sheet"]); return; }
          setRawHeaders(headers); setRawRows(rows); setMapping(autoMap(headers));
        } catch { setErrors(["Could not read the Excel file"]); }
      };
      reader.readAsArrayBuffer(selected);
      return;
    }

    // CSV (and anything else tabular) → raw + smart mapping
    reader.onload = (ev) => {
      const { headers, rows } = parseCSVRaw(ev.target.result);
      if (rows.length === 0) { setErrors(["CSV must have a header row and at least one data row"]); return; }
      setRawHeaders(headers); setRawRows(rows); setMapping(autoMap(headers));
    };
    reader.readAsText(selected);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) consumeFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) consumeFile(f);
  };

  const handleUpload = async () => {
    if (parsed.length === 0) return;

    // Send category/subcategory as NAMES — the server resolves them in the current
    // scope and CREATES any that don't exist yet (new categories AND subtopics).
    // Rows with no category at all fall back to the chosen default category.
    const rowsMissingCat = parsed.filter((q) => !String(q.category || "").trim()).length;
    if (rowsMissingCat > 0 && !defaultCategory) {
      setErrors([
        `${rowsMissingCat} question(s) have no category. Add a "category" column/field, ` +
        `or pick a default category below.`,
      ]);
      setToast({ show: true, message: "Choose a default category before uploading", type: "error" });
      return;
    }

    const payload = parsed.map((q) => ({
      ...q,
      category: String(q.category || "").trim(),
      subcategory: String(q.subcategory || "").trim(),
    }));

    setUploading(true);
    try {
      const { data } = await apiClient.post("/quiz-questions/bulk-upload", {
        questions: payload,
        defaultCategoryId: defaultCategory || undefined,
      });
      setResult(data);
      const made = data.created ?? data.inserted ?? parsed.length;
      setToast({
        show: true,
        message: `${made} questions uploaded${data.foldersCreated ? ` · ${data.foldersCreated} new folder(s)` : ""}`,
        type: "success",
      });
    } catch (err) {
      const msg = err.response?.data?.message || "Upload failed";
      const serverErrs = err.response?.data?.errors || [];
      setErrors(serverErrs.length ? serverErrs.map((e) => e.message || e) : [msg]);
      setToast({ show: true, message: msg, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const downloadSample = (type) => {
    const text = type === "json" ? SAMPLE_JSON : SAMPLE_CSV;
    const ext = type === "json" ? "json" : "csv";
    const mime = type === "json" ? "application/json" : "text/csv";
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quiz_questions_sample.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFile(null);
    setParsed([]);
    setErrors([]);
    setResult(null);
    setRawHeaders([]);
    setRawRows([]);
    setMapping({});
    if (fileRef.current) fileRef.current.value = "";
  };

  // ── The guided steps ───────────────────────────────────────────────
  // A JSON file is already questions, so it has nothing to match and skips
  // that step. The step on screen follows from what has been read so far.
  const isJson = !!file && file.name.toLowerCase().endsWith(".json");
  const steps = [
    { key: "file", title: "Choose a file" },
    ...(isJson ? [] : [{ key: "map", title: "Match columns" }]),
    { key: "check", title: "Check and upload" },
    { key: "done", title: "Done" },
  ];
  let stepKey = "file";
  if (result) stepKey = "done";
  else if (parsed.length > 0) stepKey = "check";
  else if (rawRows.length > 0) stepKey = "map";
  const current = steps.findIndex((s) => s.key === stepKey);

  // Back from the check returns to the column matching when there was one.
  const backFromCheck = () => {
    if (isJson || rawRows.length === 0) { reset(); return; }
    setErrors([]);
    setParsed([]);
  };

  const errorList = errors.length > 0 && (
    <Callout tone="danger" role="alert">
      <span className="tw-font-semibold">{errors.length} issue{errors.length > 1 ? "s" : ""} found</span>
      <ul className="tw-m-0 tw-mt-1.5 tw-flex tw-list-none tw-flex-col tw-gap-1 tw-p-0">
        {errors.slice(0, 20).map((err, i) => (
          <li key={i} className="tw-flex tw-items-center tw-gap-1.5 tw-text-[0.78rem]">
            <XCircle size={12} className="tw-shrink-0" aria-hidden="true" /> {err}
          </li>
        ))}
        {errors.length > 20 && (
          <li className="tw-text-[0.78rem] tw-italic">…and {errors.length - 20} more</li>
        )}
      </ul>
    </Callout>
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader
        icon={Upload}
        title="Bulk Upload Questions"
        description="Import many questions at once from a CSV, Excel or JSON file"
        breadcrumbs={uploadTrail({ isLibrary })}
      />

      <AppCard>
        <AppCardContent className="tw-py-3">
          <StepRail variant="bar" steps={steps} current={current} reached={current} />
        </AppCardContent>
      </AppCard>

      {/* ── 1 · Choose a file ─────────────────────────────────────── */}
      {stepKey === "file" && (
        <>
          <AppCard>
            <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
              <SectionHead
                title="Choose a file"
                sub="A CSV or Excel sheet with one question per row, or a JSON file. Any column layout works — you match the columns next."
              />
              {/* The file input is a SIBLING of the zone, not a child: an <input>
                  inside a <button> is invalid content and the click never had a
                  reason to pass through it. */}
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xlsx,.xls,.json"
                onChange={handleFileChange}
                className="tw-hidden"
                tabIndex={-1}
                aria-hidden="true"
                data-testid="bulk-file-input"
              />
              <DropZone
                size="lg"
                state={file ? "filled" : dragOver ? "active" : "idle"}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <div
                  className={cn(
                    "tw-mb-1.5 tw-inline-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors",
                    file
                      ? "tw-bg-success/[0.15] tw-text-success"
                      : "tw-bg-muted tw-text-muted-fg group-hover:tw-bg-primary/[0.12] group-hover:tw-text-primary",
                  )}
                >
                  {file
                    ? (isJson
                        ? <FileJson size={36} aria-hidden="true" />
                        : <FileSpreadsheet size={36} aria-hidden="true" />)
                    : <Upload size={36} aria-hidden="true" />}
                </div>
                <div className="tw-text-[0.95rem] tw-font-semibold tw-text-content">
                  {file ? file.name : "Drop your file here, or click to browse"}
                </div>
                <div className="tw-text-[0.78rem] tw-text-muted-fg">
                  Supports .csv, .xlsx &amp; .json · max 500 questions
                </div>
              </DropZone>

              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-text-[0.8125rem] tw-text-content-muted">Not sure of the format? Start from a sample:</span>
                <AppButton variant="outline" size="sm" onClick={() => downloadSample("csv")}>
                  <Download size={14} aria-hidden="true" /> CSV sample
                </AppButton>
                <AppButton variant="outline" size="sm" onClick={() => downloadSample("json")}>
                  <Download size={14} aria-hidden="true" /> JSON sample
                </AppButton>
              </div>

              <p className="tw-m-0 tw-flex tw-items-start tw-gap-2 tw-rounded-md tw-bg-muted/60 tw-px-3 tw-py-2.5 tw-text-[0.8125rem] tw-text-content-muted">
                <FolderTree size={16} className="tw-mt-px tw-shrink-0 tw-text-primary" aria-hidden="true" />
                <span>
                  Each question goes into the category (and subtopic) named in its own row. Rows without one go
                  into a default category you pick before uploading. Categories and subtopics that do not exist
                  yet are created for you.
                </span>
              </p>

              {errorList}
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardContent>
              <div className="tw-mb-2.5 tw-text-[0.85rem] tw-font-semibold tw-text-content">
                Supported question types — set the <code className="tw-rounded-[5px] tw-bg-muted tw-px-1.5 tw-py-px tw-text-[0.78rem] tw-text-content">questionType</code> column to one of these
              </div>
              <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(230px,1fr))] tw-gap-2">
                {SUPPORTED_TYPES.map((t) => (
                  <div
                    key={t.key}
                    className="tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-px-2.5 tw-py-2"
                  >
                    <code className="tw-inline-block tw-rounded-[5px] tw-bg-primary/[0.12] tw-px-1.5 tw-py-px tw-text-[0.72rem] tw-font-semibold tw-text-primary">
                      {t.key}
                    </code>
                    <div className="tw-mt-1 tw-text-[0.82rem] tw-font-semibold tw-text-content">{t.label}</div>
                    <div className="tw-mt-[0.15rem] tw-text-[0.74rem] tw-text-muted-fg">{t.needs}</div>
                  </div>
                ))}
              </div>
            </AppCardContent>
          </AppCard>
        </>
      )}

      {/* ── 2 · Match columns (CSV / Excel) ───────────────────────── */}
      {stepKey === "map" && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <SectionHead
              title="Match your columns"
              sub={`${rawRows.length} row${rawRows.length === 1 ? "" : "s"} found in ${file?.name || "your file"}. The columns we recognised are already matched — check each field, then continue. Only “Question text” is required.`}
            />
            <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(220px,1fr))] tw-gap-2.5">
              {TARGET_FIELDS.map((f) => (
                <FormField key={f.key} label={f.label} required={f.required} labelTone="muted">
                  <NativeSelect
                    size="sm"
                    value={mapping[f.key] || ""}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                  >
                    <option value="">— none —</option>
                    {rawHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                  </NativeSelect>
                </FormField>
              ))}
            </div>
            {errorList}
            <StepActions>
              <AppButton variant="outline" onClick={reset}>
                <ArrowLeft size={16} aria-hidden="true" /> Choose another file
              </AppButton>
              <AppButton variant="default" onClick={applyMapping}>
                Continue <ArrowRight size={16} aria-hidden="true" />
              </AppButton>
            </StepActions>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── 3 · Check and upload ──────────────────────────────────── */}
      {stepKey === "check" && (
        <AppCard>
          <AppCardContent className="tw-flex tw-flex-col tw-gap-4">
            <SectionHead
              title={`${parsed.length} questions ready`}
              sub={`Check the first ${Math.min(parsed.length, 50)} below, then upload. Missing categories & subtopics are created automatically.`}
            />
            <FormField
              label="Default category"
              hint="For rows that do not name their own category."
              className="tw-max-w-sm"
            >
              <NativeSelect
                value={defaultCategory}
                onChange={(e) => setDefaultCategory(e.target.value)}
                options={[
                  { value: "", label: "Choose a category…" },
                  ...categories
                    .filter((c) => !c.parentCategory)
                    .map((c) => ({ value: c._id, label: c.name })),
                ]}
              />
            </FormField>

            {errorList}

            <div>
              <AppDataTable
                fill={false}
                rowKey={(_, i) => i}
                rows={parsed.slice(0, 50)}
                columns={PREVIEW_COLUMNS}
              />
              {parsed.length > 50 && (
                <div className="tw--mt-px tw-rounded-b tw-border tw-border-solid tw-border-line tw-bg-muted/30 tw-p-2 tw-text-center tw-text-[0.75rem] tw-text-muted-fg">
                  Showing first 50 of {parsed.length} questions
                </div>
              )}
            </div>

            <StepActions>
              <AppButton variant="outline" onClick={backFromCheck} disabled={uploading}>
                <ArrowLeft size={16} aria-hidden="true" /> {isJson || rawRows.length === 0 ? "Choose another file" : "Back"}
              </AppButton>
              <AppButton variant="default" onClick={handleUpload} disabled={uploading}>
                <Upload size={16} aria-hidden="true" />
                {uploading ? "Uploading…" : `Upload ${parsed.length} question${parsed.length === 1 ? "" : "s"}`}
              </AppButton>
            </StepActions>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── 4 · Done ──────────────────────────────────────────────── */}
      {stepKey === "done" && (
        <AppCard>
          <AppCardContent>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3.5">
              <div className="tw-inline-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-success/[0.14] tw-text-success">
                <CheckCircle2 size={28} aria-hidden="true" />
              </div>
              <div className="tw-min-w-[220px] tw-flex-1">
                <div className="tw-text-base tw-font-medium tw-text-content">Upload complete</div>
                <div className="tw-mt-0.5 tw-text-[0.85rem] tw-text-muted-fg">
                  Successfully imported {result.created ?? result.inserted ?? parsed.length} questions
                  {result.foldersCreated ? ` · ${result.foldersCreated} new folder(s) created` : ""}
                  {result.errors?.length ? ` · ${result.errors.length} row(s) skipped` : ""}.
                </div>
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <AppButton variant="outline" onClick={reset}>Upload more</AppButton>
                <AppButton variant="default" onClick={() => navigate(base)}>
                  Go to Question Bank
                </AppButton>
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default BulkUploadPage;
